import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import exportCsv from '@/utils/exportCsv.js';
import {
  TURNOS_LIST,
  TURNO_DELETE,
} from '@/graphql/rrhh/turnos.gql.js';
import ListadoAsignacionesTurno from './ListadoAsignacionesTurno.jsx';

const INITIAL_FILTERS = {
  sucursal: '',
  activo: '',
  search: '',
};

const INITIAL_PAGINATION = {
  page: 1,
  perPage: 10,
};

const ORDENES = [
  { label: 'Más recientes', value: 'creadoEn:DESC' },
  { label: 'Nombre A-Z', value: 'nombre:ASC' },
  { label: 'Entrada más temprana', value: 'horaEntrada:ASC' },
];

function mapFilters(filters) {
  return {
    sucursalId: filters.sucursal ? Number(filters.sucursal) : null,
    activo: filters.activo === '' ? null : filters.activo === 'true',
    search: filters.search || null,
  };
}

function getEstadoClass(activo) {
  return activo
    ? 'bg-[rgba(var(--brand),0.18)] text-[rgb(var(--brand-700))]'
    : 'bg-[rgba(var(--muted),0.18)] text-[rgba(var(--muted),0.95)]';
}

function formatHorario(turno) {
  if (!turno?.horaEntrada || !turno?.horaSalida) return '—';
  return `${turno.horaEntrada.slice(0, 5)} - ${turno.horaSalida.slice(0, 5)}`;
}

export default function ListadoTurnos() {
  const navigate = useNavigate();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('turnos');
  const [filterForm, setFilterForm] = useState(INITIAL_FILTERS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [order, setOrder] = useState({ field: 'creadoEn', direction: 'DESC' });

  const [{ data, fetching, error }] = useQuery({
    query: TURNOS_LIST,
    variables: {
      filters: mapFilters(filters),
      pagination,
      order,
    },
    pause: activeTab !== 'turnos',
  });

  const [, deleteTurno] = useMutation(TURNO_DELETE);

  const turnos = data?.turnos?.items ?? [];
  const pageInfo = data?.turnos?.pageInfo ?? { ...pagination, totalItems: turnos.length, totalPages: 1 };
  const totalTurnos = data?.turnos?.total ?? turnos.length;

  const handleSubmitFilters = (event) => {
    event.preventDefault();
    setFilters(filterForm);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleOrderChange = (event) => {
    const [field, direction] = event.target.value.split(':');
    setOrder({ field, direction });
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('¿Eliminar el turno seleccionado?');
    if (!confirmed) return;
    const result = await deleteTurno({ id });
    if (result.error) {
      toast.error(result.error?.message || 'No pudimos eliminar el turno.');
      return;
    }
    toast.success('El turno fue eliminado.');
  };

  const handleExport = () => {
    if (!turnos.length) {
      toast.info('No hay turnos para exportar con los filtros actuales.');
      return;
    }
    exportCsv({
      filename: 'turnos.csv',
      columns: [
        { label: 'ID', key: 'id' },
        { label: 'Nombre', key: 'nombre' },
        { label: 'Descripción', key: 'descripcion' },
        { label: 'Hora entrada', key: 'horaEntrada' },
        { label: 'Hora salida', key: 'horaSalida' },
        { label: 'Descanso (min)', key: 'descansoMinutos' },
        { label: 'Fines de semana', key: 'aplicaFinesDeSemana' },
        { label: 'Sucursal', key: 'sucursalNombre' },
        { label: 'Activo', key: 'activo' },
      ],
      rows: turnos,
    });
    toast.success('Exportamos el CSV con los turnos listados.');
  };

  const summaryText = useMemo(() => {
    return `${totalTurnos} turnos configurados`;
  }, [totalTurnos]);

  return (
    <div className="w-full">
      <PageHeader
        title="Turnos"
        description="Definí horarios estándar y asignalos a los equipos."
        breadcrumbs={[
          { label: 'RRHH', href: '/rrhh' },
          { label: 'Turnos' },
        ]}
        actions={(
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate('/rrhh/turnos/asignaciones/nueva')}
              className="rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--panel),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Nueva asignación
            </button>
            <button
              type="button"
              onClick={() => navigate('/rrhh/turnos/nuevo')}
              className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] hover:bg-[rgb(var(--brand-700))]"
            >
              Nuevo turno
            </button>
          </div>
        )}
      >
        <p className="text-sm text-[rgba(var(--muted),0.9)]">{summaryText}</p>
      </PageHeader>

      <section className="mx-auto w-full max-w-[1400px] space-y-6">
        <nav className="flex flex-wrap gap-2 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.6)] p-2 shadow-[0_6px_24px_rgba(var(--shadow),0.12)]">
          <button
            type="button"
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${activeTab === 'turnos' ? 'bg-[rgb(var(--brand))] text-white shadow-md' : 'bg-transparent text-[rgba(var(--muted),0.95)] hover:bg-[rgba(var(--panel),0.8)]'}`}
            aria-current={activeTab === 'turnos' ? 'page' : undefined}
            onClick={() => setActiveTab('turnos')}
          >
            Turnos
          </button>
          <button
            type="button"
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${activeTab === 'asignaciones' ? 'bg-[rgb(var(--brand))] text-white shadow-md' : 'bg-transparent text-[rgba(var(--muted),0.95)] hover:bg-[rgba(var(--panel),0.8)]'}`}
            aria-current={activeTab === 'asignaciones' ? 'page' : undefined}
            onClick={() => setActiveTab('asignaciones')}
          >
            Asignaciones
          </button>
        </nav>

        {activeTab === 'turnos' ? (
          <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.95)] p-6 shadow-[0_8px_30px_rgba(var(--shadow),0.15)]">
            <form onSubmit={handleSubmitFilters} className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-4">
                <Field label="Buscar" htmlFor="search" hint="Nombre o descripción">
                  <input
                    id="search"
                    value={filterForm.search}
                    onChange={event => setFilterForm(prev => ({ ...prev, search: event.target.value }))}
                    placeholder="Ej: Turno mañana"
                    className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  />
                </Field>
              </div>
              <div className="col-span-12 md:col-span-4">
                <Field label="Sucursal" htmlFor="sucursal">
                  <input
                    id="sucursal"
                    value={filterForm.sucursal}
                    onChange={event => setFilterForm(prev => ({ ...prev, sucursal: event.target.value }))}
                    placeholder="ID sucursal"
                    className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  />
                </Field>
              </div>
              <div className="col-span-12 sm:col-span-6 md:col-span-2">
                <Field label="Activo" htmlFor="activo">
                  <select
                    id="activo"
                    value={filterForm.activo}
                    onChange={event => setFilterForm(prev => ({ ...prev, activo: event.target.value }))}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  >
                    <option value="">Todos</option>
                    <option value="true">Activos</option>
                    <option value="false">Inactivos</option>
                  </select>
                </Field>
              </div>
              <div className="col-span-12 sm:col-span-6 md:col-span-2">
                <Field label="Orden" htmlFor="order">
                  <select
                    id="order"
                    value={`${order.field}:${order.direction}`}
                    onChange={handleOrderChange}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  >
                    {ORDENES.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-span-12 flex flex-wrap items-end justify-between gap-3">
                <button
                  type="submit"
                  className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] hover:bg-[rgb(var(--brand-700))]"
                >
                  Aplicar filtros
                </button>
                <button
                  type="button"
                  onClick={handleExport}
                  className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  Exportar CSV
                </button>
              </div>
            </form>

            <div className="mt-8 overflow-x-auto">
              <table className="min-w-full table-auto border-separate border-spacing-y-2">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-[rgba(var(--muted),0.9)]">
                    <th className="px-3 py-2">Nombre</th>
                    <th className="px-3 py-2">Horario</th>
                    <th className="px-3 py-2">Descanso</th>
                    <th className="px-3 py-2">Fines de semana</th>
                    <th className="px-3 py-2">Sucursal</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {fetching ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.9)]">
                        Cargando turnos...
                      </td>
                    </tr>
                  ) : null}

                  {error ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.9)]">
                        No pudimos cargar los turnos.
                      </td>
                    </tr>
                  ) : null}

                  {!fetching && !turnos.length ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-6">
                        <EmptyState
                          title="Sin turnos registrados"
                          description="Crea un turno para empezar a gestionar las asignaciones." />
                      </td>
                    </tr>
                  ) : null}

                  {turnos.map(turno => (
                    <tr key={turno.id} className="rounded-2xl bg-[rgba(var(--subpanel),0.35)] text-sm text-[rgb(var(--fg))] shadow-[0_4px_16px_rgba(var(--shadow),0.06)]">
                      <td className="rounded-l-2xl px-3 py-3 align-top">
                        <div className="flex flex-col">
                          <span className="font-semibold">{turno.nombre}</span>
                          <span className="text-xs text-[rgba(var(--muted),0.9)]">{turno.descripcion}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top">{formatHorario(turno)}</td>
                      <td className="px-3 py-3 align-top">{turno.descansoMinutos} min</td>
                      <td className="px-3 py-3 align-top">{turno.aplicaFinesDeSemana ? 'Sí' : 'No'}</td>
                      <td className="px-3 py-3 align-top">{turno.sucursalNombre || turno.sucursalId || '—'}</td>
                      <td className="px-3 py-3 align-top">
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getEstadoClass(turno.activo)}`}>
                          {turno.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="rounded-r-2xl px-3 py-3 align-top">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/rrhh/turnos/${turno.id}/editar`)}
                            className="rounded-lg border border-[rgba(var(--border),0.4)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--panel),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(turno.id)}
                            className="rounded-lg bg-[rgba(var(--fg),0.08)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-[rgba(var(--muted),0.9)]">
                Mostrando {turnos.length} de {totalTurnos} turnos
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}
                  disabled={(pageInfo.page || 1) <= 1}
                  className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-1.5 text-sm font-medium text-[rgba(var(--muted),0.95)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Anterior
                </button>
                <span className="text-sm font-medium text-[rgb(var(--fg))]">
                  Página {pageInfo.page || 1} de {pageInfo.totalPages || 1}
                </span>
                <button
                  type="button"
                  onClick={() => setPagination(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
                  disabled={(pageInfo.page || 1) >= (pageInfo.totalPages || 1)}
                  className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-1.5 text-sm font-medium text-[rgba(var(--muted),0.95)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <ListadoAsignacionesTurno
          active={activeTab === 'asignaciones'}
          onCreate={() => navigate('/rrhh/turnos/asignaciones/nueva')}
          onEdit={id => navigate(`/rrhh/turnos/asignaciones/${id}/editar`)}
        />
      </section>
    </div>
  );
}
